import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Zap, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = '/api';

export default function AIChatSidebar({ reportId, sessionId }) {
  const { i18n, t } = useTranslation();
  const [chatMessages, setChatMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [askingAI, setAskingAI] = useState(false);

  const handleAskAI = async () => {
    if (!question.trim() || askingAI) return;

    const userMessage = { role: 'user', content: question };
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    setQuestion('');
    setAskingAI(true);

    try {
      const response = await axios.post(`${API}/ai/ask`, {
        report_id: reportId,
        question: question,
        session_id: sessionId,
        language: i18n.language,
      });
      setChatMessages([...updatedMessages, { role: 'assistant', content: response.data.answer }]);
    } catch {
      toast.error('AI error');
    } finally {
      setAskingAI(false);
    }
  };

  const suggestedQuestions = [
    { label: t('ai.q1Label'), q: t('ai.q1') },
    { label: t('ai.q2Label'), q: t('ai.q2') },
    { label: t('ai.q3Label'), q: t('ai.q3') },
  ];

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm sticky top-24" data-testid="ai-assistant">
      <div className="p-4 border-b bg-primary text-white rounded-t-xl">
        <h3 className="font-medium flex items-center gap-2">
          <Zap className="w-5 h-5" />
          {t('ai.title')}
        </h3>
        <p className="text-xs text-white/80 mt-1">{t('ai.subtitle')}</p>
      </div>

      <div className="p-4 h-96 overflow-y-auto" data-testid="chat-messages">
        {chatMessages.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-8">
            <p className="mb-4">{t('ai.emptyPrompt')}</p>
            <div className="space-y-2 text-left">
              {suggestedQuestions.map((item, idx) => (
                <button
                  key={item.label}
                  onClick={() => setQuestion(item.q)}
                  className="w-full text-left p-2 text-xs bg-muted rounded hover:bg-muted/80 transition-colors"
                  data-testid={`suggested-question-${idx + 1}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {chatMessages.map((msg, idx) => (
              <div key={`msg-${sessionId}-${idx}`} className={msg.role === 'user' ? 'text-right' : 'text-left'} data-testid={`chat-message-${idx}`}>
                <div className={`inline-block p-3 rounded-lg max-w-[85%] ${
                  msg.role === 'user' ? 'bg-primary text-white' : 'bg-muted text-foreground'
                }`}>
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            ))}
            {askingAI && (
              <div className="text-left">
                <div className="inline-block p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">{t('ai.thinking')}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAskAI()}
            placeholder={t('ai.placeholder')}
            disabled={askingAI}
            data-testid="ai-question-input"
          />
          <button
            onClick={handleAskAI}
            disabled={askingAI || !question.trim()}
            className="btn-primary px-4 py-2 rounded-md disabled:opacity-50"
            data-testid="ai-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
